/**
 * Which hostile a click/tap lands on (docs/design/combat.md §Control model, "Explicit
 * target"; AD-817). Screen-space, so the tolerance is what a finger or a cursor can
 * actually hit: every hostile is projected to a vertical segment (feet → head) with a
 * body radius in pixels, and the pick is the nearest segment within the tolerance.
 *
 * Why not a mesh raycast: enemy bodies are rig proxies or cone placeholders of varied
 * size, some thin; a fingertip covers ~7–10 mm (≈ 40 CSS px), so an exact-mesh test
 * misses most real taps. A pixel tolerance around a body segment is the standard
 * forgiveness, and it is cheap (a handful of projections per press).
 *
 * Pure (no THREE, no DOM): the Game projects, this decides.
 */

/** Pick tolerance for a coarse pointer (touch), CSS px — about a fingertip's radius. */
export const PICK_TOLERANCE_COARSE_PX = 32;
/** Pick tolerance for a fine pointer (mouse, pen), CSS px. */
export const PICK_TOLERANCE_FINE_PX = 12;
/** The body height a hostile is projected with, metres (feet → head; most enemies are humanoid-sized). */
export const PICK_BODY_HEIGHT_M = 1.6;
/** The body half-width a hostile is projected with, metres. */
export const PICK_BODY_RADIUS_M = 0.45;

export type PointerKind = 'mouse' | 'pen' | 'touch';

export function pickTolerancePx(kind: PointerKind): number {
  return kind === 'touch' ? PICK_TOLERANCE_COARSE_PX : PICK_TOLERANCE_FINE_PX;
}

/** A hostile as the screen sees it: feet and head points in canvas px, body radius in px, camera depth. */
export interface ScreenHostile {
  id: number;
  footX: number;
  footY: number;
  headX: number;
  headY: number;
  radiusPx: number;
  /** Distance from the camera, metres — the tie-break (the nearer body is in front). */
  depth: number;
}

/** Distance from (px, py) to the segment (ax, ay)–(bx, by). */
export function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/**
 * The hostile under (px, py): the smallest gap between the point and a body (segment
 * distance minus body radius, floored at 0) that is ≤ `tolerancePx`; equal gaps go to
 * the body nearer the camera. Null when none is close enough.
 */
export function pickHostileAt(
  px: number,
  py: number,
  candidates: Iterable<ScreenHostile>,
  tolerancePx: number,
): number | null {
  let best: { id: number; gap: number; depth: number } | null = null;
  for (const c of candidates) {
    const gap = Math.max(
      0,
      distanceToSegment(px, py, c.footX, c.footY, c.headX, c.headY) - c.radiusPx,
    );
    if (gap > tolerancePx) continue;
    if (best === null || gap < best.gap || (gap === best.gap && c.depth < best.depth)) {
      best = { id: c.id, gap, depth: c.depth };
    }
  }
  return best?.id ?? null;
}
